import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, keyframes, styled } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";

// Custom styled components with animations
const ProjectsContainer = styled(Box)({
  width: "100%",
  minHeight: "80vh",
  padding: "40px 20px",
  // background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
  animation: `${keyframes`
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  `} 0.6s ease-in-out`,
});

const ProjectsWrapper = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: "40px",
  maxWidth: "1400px",
  margin: "0 auto",
  alignItems: "start",
  "@media (max-width: 1024px)": {
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "30px",
  },
  "@media (max-width: 768px)": {
    gridTemplateColumns: "1fr",
    gap: "25px",
  },
});

const ProjectBox = styled(Box)(({ delay }) => ({
  animation: `${keyframes`
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `} 0.8s ease-out both`,
  animationDelay: delay,
  perspective: "1000px",
}));

const ProjectCard = styled(Box)({
  position: "relative",
  width: "80%",
  height: "180px", // Increased height for larger logos
  transformStyle: "preserve-3d",
  cursor: "pointer",
  "@media (max-width: 1024px)": {
    height: "260px",
  },
  "@media (max-width: 768px)": {
    height: "240px",
  },
  "@media (max-width: 480px)": {
    height: "220px",
  },
});

const ProjectSide = styled(Box)({
  position: "absolute",
  width: "100%",
  height: "100%",
  backfaceVisibility: "hidden",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "25px",
  borderRadius: "15px",
  textAlign: "center",
  "@media (max-width: 768px)": {
    padding: "20px",
  },
  "@media (max-width: 480px)": {
    padding: "15px",
  },
});

const ProjectFront = styled(ProjectSide)({
  color: "white",
  boxShadow: "0 8px 25px rgba(45, 134, 89, 0.2)",
  border: "2px solid rgba(255, 255, 255, 0.2)",
  background: "rgba(0, 0, 0, 0.4)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  "&:hover": {
    transform: "scale(1.1)",
  },
});

const ProjectIcon = styled(Box)({
  width: "80px", // Fixed width for consistent sizing
  height: "80px", // Fixed height for consistent sizing
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "15px",
  "@media (max-width: 768px)": {
    width: "70px",
    height: "70px",
  },
  "@media (max-width: 480px)": {
    width: "60px",
    height: "60px",
  },
});

const ProjectLogoImg = styled("img")({
  width: "100%", // Fill the container
  height: "100%", // Fill the container
  filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2))",
});

const ProjectTitle = styled(Typography)({
  fontSize: "1.4rem",
  fontWeight: 700,
  marginBottom: "10px",
  letterSpacing: "1px",
  textAlign: "center",
  "@media (max-width: 768px)": {
    fontSize: "1.2rem",
  },
  "@media (max-width: 480px)": {
    fontSize: "1.1rem",
  },
});

const ProjectTagline = styled(Typography)({
  fontSize: "0.9rem",
  marginBottom: "15px",
  fontWeight: 500,
  lineHeight: 1.4,
  opacity: 0.95,
  textAlign: "center",
  "@media (max-width: 768px)": {
    fontSize: "0.8rem",
  },
  "@media (max-width: 480px)": {
    fontSize: "0.75rem",
  },
});

const Header = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  // background: "linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%)",
  padding: "5px 30px",
  marginBottom: "10px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  gap: "5px",
  "@media (max-width: 768px)": {
    padding: "5px 15px",
  },
});

const LogoImage = styled("img")({
  maxWidth: "100%",
  height: "auto",
  // aspectRatio: "150 / 130",
  objectFit: "contain",
  objectPosition: "center",
  filter: "drop-shadow(0 2px 8px rgba(45, 134, 89, 0.15))",
});

const BackButton = styled(Button)({
  color: "white",
  border: "2px solid rgba(255, 255, 255, 0.3)",
  padding: "10px 25px",
  borderRadius: "25px",
  fontSize: "0.95rem",
  cursor: "pointer",
  marginBottom: "30px",
  boxShadow: "0 4px 15px rgba(45, 134, 89, 0.2)",
  "&:hover": {
    borderColor: "rgba(255, 255, 255, 0.5)",
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(45, 134, 89, 0.4)",
  },
  "@media (max-width: 768px)": {
    padding: "8px 20px",
    fontSize: "0.85rem",
  },
  "@media (max-width: 480px)": {
    padding: "7px 18px",
    fontSize: "0.75rem",
  },
});

const SWM = () => {
  const navigate = useNavigate();

  const projects = [
    {
      id: 1,
      title: "Suthra Punjab",
      tagline: "World's Largest Waste Management System",
      link: "/SuthraPunjab",
      logo: "/images/SuthraPunjab.png",
    },
    {
      id: 2,
      title: "Digital Monitoring",
      tagline: "Real-time Analytics & Tracking",
      link: "/DigitalMonitoring",
      logo: "/images/DigitalMonitoring.png",
    },
    {
      id: 3,
      title: "Waste-to-Value",
      tagline: "Converting Waste into Resources",
      link: "/WasteDashboard",
      logo: "/images/WasteToValue.png",
    },
  ];

  const handleProjectClick = (project) => {
    navigate(project.link);
  };

  return (
    <>
      <ProjectsContainer>
        {/* Header */}
        <Header>
          <Box
            sx={{
              width: "8%",
              height: "8%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "white",
              borderRadius: "50%",
              p: 1,
              opacity: 0.8,
            }}
          >
            <Box className="logo-placeholder">
              <LogoImage src="images/PunjabLogo.png" alt="Suthra Punjab Logo" />
            </Box>
          </Box>

          <Box sx={{ textAlign: "center", flex: 1, px: 25 }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: "2.5rem",
                fontWeight: 800,
                color: "#2d8659",
                bgcolor: "white",
                letterSpacing: "3px",
                margin: "8px 0",
                borderRadius: "15px",
                opacity: 0.8,
                textTransform: "uppercase",
                "@media (max-width: 768px)": {
                  fontSize: "2rem",
                },
                "@media (max-width: 480px)": {
                  fontSize: "1.6rem",
                },
              }}
            >
              Solid Waste Management
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: "#555",
                fontSize: "1rem",
                fontWeight: 500,
                letterSpacing: "0.5px",
                bgcolor: "white",
                borderRadius: "15px",
                opacity: 0.8,
              }}
            >
              World's Largest Waste Management System
            </Typography>
          </Box>

          <Box
            sx={{
              width: "8%",
              height: "8%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "white",
              borderRadius: "50%",
              padding: 2,
              opacity: 0.8,
            }}
          >
            <Box className="logo-red-placeholder">
              <LogoImage src="images/CM.png" alt="CM Logo" />
            </Box>
          </Box>
        </Header>
        <BackButton startIcon={<ArrowBack />} onClick={() => navigate("/")}>
          Back
        </BackButton>

        {/* Main Projects Grid - 3 boxes in a row */}
        <ProjectsWrapper>
          {projects.map((project, index) => (
            <ProjectBox key={project.id}>
              <ProjectCard>
                <ProjectFront onClick={() => handleProjectClick(project)}>
                  <ProjectIcon>
                    <ProjectLogoImg src={project.logo} alt="Logo" />
                  </ProjectIcon>
                  <ProjectTitle variant="h3">{project.title}</ProjectTitle>
                  <ProjectTagline variant="body1">
                    {project.tagline}
                  </ProjectTagline>
                </ProjectFront>
              </ProjectCard>
            </ProjectBox>
          ))}
        </ProjectsWrapper>
      </ProjectsContainer>
    </>
  );
};

export default SWM;
